import React, { useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { ChevronDown } from "lucide-react";
import { GlobalButton } from "../forms/GlobalFormFields";
import "./globalDirectory.css";

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildTableHtml(headers, rows, { compact } = {}) {
  const th = compact
    ? 'style="border:1px solid #ccc;padding:4px;font-size:10px;text-align:left"'
    : 'style="border:1px solid #ccc;padding:6px;text-align:left"';
  const td = compact
    ? 'style="border:1px solid #ccc;padding:4px;font-size:10px"'
    : 'style="border:1px solid #ccc;padding:6px"';
  const head = `<thead><tr>${headers
    .map((h) => `<th ${th}>${escapeHtml(h)}</th>`)
    .join("")}</tr></thead>`;
  const body = `<tbody>${rows
    .map(
      (r) =>
        `<tr>${r
          .map((c) => `<td ${td}>${escapeHtml(c).replace(/\n/g, "<br/>")}</td>`)
          .join("")}</tr>`
    )
    .join("")}</tbody>`;
  return `<table style="border-collapse:collapse;width:100%">${head}${body}</table>`;
}

function downloadExcelHtmlTable(filenameBase, headers, rows) {
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8" /></head><body>${buildTableHtml(
    headers,
    rows,
    {}
  )}</body></html>`;
  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenameBase}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvEscapeCell(text) {
  const s = String(text ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filenameBase, headers, rows) {
  const lines = [
    headers.map(csvEscapeCell).join(","),
    ...rows.map((cells) => cells.map(csvEscapeCell).join(",")),
  ];
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenameBase}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function downloadPdf(filenameBase, headers, rows) {
  const wrap = document.createElement("div");
  wrap.style.padding = "12px";
  wrap.style.background = "#fff";
  wrap.innerHTML = `<h2 style="font-size:14px;margin:0 0 10px">${escapeHtml(
    filenameBase
  )}</h2>${buildTableHtml(headers, rows, { compact: true })}`;
  wrap.setAttribute("data-gds-export-pdf", "1");
  document.body.appendChild(wrap);
  try {
    await html2pdf()
      .set({
        margin: 8,
        filename: `${filenameBase}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] }
      })
      .from(wrap)
      .save();
  } finally {
    wrap.remove();
  }
}

/**
 * Export dropdown: CSV, PDF, and Excel (.xls). `getExportData` returns { headers, rows }
 * where `headers` are strings and `rows` are string[][].
 */
export default function GlobalDirectoryExportMenu({
  getExportData,
  filenameBase = "export",
  buttonLabel = "Export as"
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const run = async (kind) => {
    setOpen(false);
    const data = getExportData?.();
    if (!data?.headers?.length) return;
    const { headers, rows } = data;
    const base = String(filenameBase || "export").replace(/[^\w.-]+/g, "_");
    if (kind === "csv") {
      downloadCsv(base, headers, rows);
    } else if (kind === "excel") {
      downloadExcelHtmlTable(base, headers, rows);
    } else if (kind === "pdf") {
      await downloadPdf(base, headers, rows);
    }
  };

  return (
    <div className="gds-export-wrap" ref={ref}>
      <GlobalButton
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="gds-directory-toolbar-btn"
      >
        {buttonLabel}
        <ChevronDown size={14} className="gds-export__chev" aria-hidden />
      </GlobalButton>
      {open ? (
        <div className="gds-export__menu" role="menu">
          <button type="button" role="menuitem" onClick={() => run("csv")}>
            Download as CSV
          </button>
          <button type="button" role="menuitem" onClick={() => run("pdf")}>
            Download as PDF
          </button>
          <button type="button" role="menuitem" onClick={() => run("excel")}>
            Download as Excel (.xls)
          </button>
        </div>
      ) : null}
    </div>
  );
}
